import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, Trash2, Copy, ChevronDown, ChevronUp } from 'lucide-react';
import { PrizePool, PrizeDataStructure, PrizeTab, PrizeEntry, validatePrizeData } from '@/components/PrizePool';
import { TimezoneSelect } from '@/components/TimezoneSelect';

// Converts ISO (YYYY-MM-DDTHH:MM) → digit string (DDMMYYYYHHMM)
function isoToDigits(iso: string): string {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!m) return '';
  return m[3] + m[2] + m[1] + m[4] + m[5];
}

// Converts digit string (DDMMYYYYHHMM, 12 chars) → ISO (YYYY-MM-DDTHH:MM)
function digitsToISO(digits: string): string {
  if (digits.length < 12) return '';
  return `${digits.slice(4, 8)}-${digits.slice(2, 4)}-${digits.slice(0, 2)}T${digits.slice(8, 10)}:${digits.slice(10, 12)}`;
}

// Formats digit string into DD/MM/YYYY HH:MM display
function digitsToDisplay(digits: string): string {
  let r = '';
  for (let i = 0; i < digits.length && i < 12; i++) {
    if (i === 2 || i === 4) r += '/';
    else if (i === 8) r += ' ';
    else if (i === 10) r += ':';
    r += digits[i];
  }
  return r;
}

function isValidISO(iso: string): boolean {
  if (!iso) return true; // empty = valid
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
  if (!m) return false;
  const [, , mm, dd, hh, min] = m.map(Number);
  return mm >= 1 && mm <= 12 && dd >= 1 && dd <= 31 && hh <= 23 && min <= 59;
}

interface DateTimeInputProps {
  value: string; // ISO format: YYYY-MM-DDTHH:MM or empty
  onChange: (iso: string) => void;
  error?: boolean;
}

const DateTimeInput = ({ value, onChange, error }: DateTimeInputProps) => {
  const lastEmittedISO = useRef(value);
  const [display, setDisplay] = useState(() => digitsToDisplay(isoToDigits(value)));

  useEffect(() => {
    if (value !== lastEmittedISO.current) {
      setDisplay(digitsToDisplay(isoToDigits(value)));
    }
  }, [value]);

  const commit = (digits: string) => {
    const iso = digitsToISO(digits);
    lastEmittedISO.current = iso;
    onChange(iso);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/[^0-9]/g, '').slice(0, 12);
    setDisplay(digitsToDisplay(digits));
    commit(digits);
  };

  const handleBlur = () => {
    const digits = display.replace(/[^0-9]/g, '');
    // If user entered only the date part (8 digits), default time to 00:00
    if (digits.length >= 8 && digits.length < 12) {
      const padded = digits.slice(0, 8) + '0000';
      setDisplay(digitsToDisplay(padded));
      commit(padded);
    }
  };

  return (
    <Input
      type="text"
      value={display}
      onChange={handleChange}
      onBlur={handleBlur}
      placeholder="DD/MM/AAAA HH:MM"
      maxLength={16}
      className={error ? 'border-red-500 focus-visible:ring-red-500' : ''}
    />
  );
};

interface PrizeDataBuilderProps {
  value: string;
  onChange: (json: string) => void;
  onValidationChange?: (isValid: boolean) => void;
}

const EMPTY_STRUCTURE: PrizeDataStructure = { tabs: [], footer: '' };

function parseValue(v: string): PrizeDataStructure {
  try {
    const p = JSON.parse(v || '{}');
    // Already valid new format
    if (validatePrizeData(p)) return p;
    // Handle old format: { "TabName": [{position, prize}] }
    if (p && !p.tabs && typeof p === 'object') {
      const tabs: PrizeTab[] = Object.entries(p).map(([label, prizes]) => ({
        label,
        prizes: (prizes as PrizeEntry[]) || [],
        bonus: '',
      }));
      const converted = { tabs, footer: '' };
      if (validatePrizeData(converted)) return converted;
    }
    return EMPTY_STRUCTURE;
  } catch {
    return EMPTY_STRUCTURE;
  }
}

const PrizeDataBuilder = ({ value, onChange, onValidationChange }: PrizeDataBuilderProps) => {
  const { t } = useTranslation();
  const [data, setData] = useState<PrizeDataStructure>(() => parseValue(value));
  const [jsonOpen, setJsonOpen] = useState(false);
  const [jsonText, setJsonText] = useState('');
  const [jsonError, setJsonError] = useState('');
  const lastEmittedRef = useRef<string>('');

  // Sync when value changes from outside (e.g. opening a different challenge)
  useEffect(() => {
    if (value !== lastEmittedRef.current) {
      setData(parseValue(value));
    }
  }, [value]);

  const computeValidity = (d: PrizeDataStructure) =>
    d.tabs.every(tab =>
      isValidISO(tab.start_date || '') &&
      isValidISO(tab.end_date || '')
    );

  const emit = (newData: PrizeDataStructure) => {
    setData(newData);
    const json = JSON.stringify(newData);
    lastEmittedRef.current = json;
    onChange(json);
    onValidationChange?.(computeValidity(newData));
  };

  const ordinalPosition = (n: number) => {
    const suffixes: Record<number, string> = { 1: '1º LUGAR', 2: '2º LUGAR', 3: '3º LUGAR' };
    return suffixes[n] || `${n}º LUGAR`;
  };

  const addTab = () => {
    const tabNumber = data.tabs.length + 1;
    emit({
      ...data,
      tabs: [...data.tabs, { label: `Semana ${tabNumber}`, prizes: [{ position: ordinalPosition(1), prize: '' }], bonus: '' }],
    });
  };

  const removeTab = (i: number) => {
    emit({ ...data, tabs: data.tabs.filter((_, idx) => idx !== i) });
  };

  const updateTabField = (i: number, field: keyof PrizeTab, val: string) => {
    const tabs = data.tabs.map((tab, idx) => idx === i ? { ...tab, [field]: val } : tab);
    emit({ ...data, tabs });
  };

  const addPrize = (tabIdx: number) => {
    const tab = data.tabs[tabIdx];
    const nextPosition = ordinalPosition(tab.prizes.length + 1);
    const tabs = data.tabs.map((t, i) =>
      i === tabIdx ? { ...t, prizes: [...t.prizes, { position: nextPosition, prize: '' }] } : t
    );
    emit({ ...data, tabs });
  };

  const removePrize = (tabIdx: number, prizeIdx: number) => {
    const tabs = data.tabs.map((tab, i) =>
      i === tabIdx ? { ...tab, prizes: tab.prizes.filter((_, j) => j !== prizeIdx) } : tab
    );
    emit({ ...data, tabs });
  };

  const updatePrize = (tabIdx: number, prizeIdx: number, field: keyof PrizeEntry, val: string) => {
    const tabs = data.tabs.map((tab, i) =>
      i === tabIdx
        ? { ...tab, prizes: tab.prizes.map((p, j) => j === prizeIdx ? { ...p, [field]: val } : p) }
        : tab
    );
    emit({ ...data, tabs });
  };

  return (
    <div className="space-y-4">
      {/* Tabs builder */}
      <div className="space-y-3">
        {data.tabs.map((tab, tabIdx) => (
          <div key={tabIdx} className="border border-border rounded-lg p-4 space-y-3">
            {/* Tab header */}
            <div className="flex items-center gap-2">
              <Input
                value={tab.label}
                onChange={(e) => updateTabField(tabIdx, 'label', e.target.value)}
                placeholder={t('admin.challenges.prizeBuilder.tabName')}
                className="font-medium"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => removeTab(tabIdx)}
                className="text-red-400 shrink-0"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>

            {/* Prizes */}
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground uppercase tracking-wide">
                {t('admin.challenges.prizeBuilder.prizes')}
              </Label>
              {tab.prizes.map((prize, prizeIdx) => (
                <div key={prizeIdx} className="flex gap-2 items-center">
                  <Input
                    value={prize.position}
                    onChange={(e) => updatePrize(tabIdx, prizeIdx, 'position', e.target.value)}
                    placeholder={t('admin.challenges.prizeBuilder.position')}
                    className="w-40 shrink-0"
                  />
                  <Input
                    value={prize.prize}
                    onChange={(e) => updatePrize(tabIdx, prizeIdx, 'prize', e.target.value)}
                    placeholder={t('admin.challenges.prizeBuilder.prize')}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removePrize(tabIdx, prizeIdx)}
                    className="text-red-400 shrink-0"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => addPrize(tabIdx)}
                className="border-dashed w-full"
              >
                <Plus className="w-3 h-3 mr-1" />
                {t('admin.challenges.prizeBuilder.addPrize')}
              </Button>
            </div>

            {/* Bonus */}
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground uppercase tracking-wide">
                🎁 {t('admin.challenges.prizeBuilder.bonus')}
              </Label>
              <Input
                value={tab.bonus || ''}
                onChange={(e) => updateTabField(tabIdx, 'bonus', e.target.value)}
                placeholder={t('admin.challenges.prizeBuilder.bonusPlaceholder')}
              />
            </div>

            {/* Period & Timezone */}
            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground uppercase tracking-wide">
                  {t('admin.challenges.prizeBuilder.startDate')}
                </Label>
                <DateTimeInput
                  value={tab.start_date || ''}
                  onChange={(v) => updateTabField(tabIdx, 'start_date', v)}
                  error={!isValidISO(tab.start_date || '')}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground uppercase tracking-wide">
                  {t('admin.challenges.prizeBuilder.endDate')}
                </Label>
                <DateTimeInput
                  value={tab.end_date || ''}
                  onChange={(v) => updateTabField(tabIdx, 'end_date', v)}
                  error={!isValidISO(tab.end_date || '')}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground uppercase tracking-wide">
                  {t('admin.challenges.prizeBuilder.timezone')}
                </Label>
                <TimezoneSelect
                  value={tab.timezone || 'America/Sao_Paulo'}
                  onChange={(val) => updateTabField(tabIdx, 'timezone', val)}
                />
              </div>
            </div>
          </div>
        ))}

        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addTab}
          className="border-dashed w-full"
        >
          <Plus className="w-4 h-4 mr-2" />
          {t('admin.challenges.prizeBuilder.addTab')}
        </Button>
      </div>

      {/* Footer note */}
      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground uppercase tracking-wide">
          {t('admin.challenges.prizeBuilder.footer')}
        </Label>
        <Input
          value={data.footer || ''}
          onChange={(e) => emit({ ...data, footer: e.target.value })}
          placeholder={t('admin.challenges.prizeBuilder.footerPlaceholder')}
        />
      </div>

      {/* Live preview */}
      {data.tabs.length > 0 && (
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground uppercase tracking-wide">
            {t('admin.challenges.prizeBuilder.preview')}
          </Label>
          <PrizePool
            data={data}
            title={t('challenge.prizePool')}
            subtitle={t('challenge.prizePoolSubtitle')}
          />
        </div>
      )}

      {/* JSON viewer / importer */}
      <div className="border border-border rounded-lg overflow-hidden">
        <button
          type="button"
          onClick={() => {
            const next = !jsonOpen;
            setJsonOpen(next);
            if (next) {
              setJsonText(JSON.stringify(data, null, 2));
              setJsonError('');
            }
          }}
          className="w-full flex items-center justify-between px-3 py-2 text-xs text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
        >
          <span className="font-mono uppercase tracking-wide">{t('admin.challenges.prizeBuilder.viewJson')}</span>
          {jsonOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>
        {jsonOpen && (
          <div className="p-3 space-y-2 bg-muted/30">
            <textarea
              className="w-full text-xs font-mono bg-background border border-border rounded p-2 h-48 resize-y focus:outline-none focus:ring-1 focus:ring-primary"
              value={jsonText}
              onChange={(e) => { setJsonText(e.target.value); setJsonError(''); }}
              spellCheck={false}
            />
            {jsonError && <p className="text-xs text-red-400">{jsonError}</p>}
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="text-xs"
                onClick={() => {
                  const parsed = parseValue(jsonText);
                  if (parsed === EMPTY_STRUCTURE && jsonText.trim() !== '' && jsonText.trim() !== '{}') {
                    setJsonError(t('admin.challenges.prizeBuilder.jsonInvalid'));
                    return;
                  }
                  emit(parsed);
                  setJsonError('');
                }}
              >
                {t('admin.challenges.prizeBuilder.applyJson')}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-xs"
                onClick={() => navigator.clipboard.writeText(jsonText)}
              >
                <Copy className="w-3 h-3 mr-1" />
                {t('admin.challenges.prizeBuilder.copy')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PrizeDataBuilder;
